// chatState.ts — the Sessions page's derived decisions, kept out of the
// component so they can be tested without rendering one.
//
//   liveBubbleIndex  which bubble the model is still working on, and so
//                    which one gets the spinner.
//   sendRoute        what pressing send does right now.
//   turnGapRem       how much air a turn gets above it.
//   byRecency        the order the sidebar lists conversations in.
//   formatRecency    the age a sidebar badge prints for a session.
//
// The first two look like one-liners and neither is. The obvious answers
// — "the last message" and "send it" — are wrong in states the user
// reaches routinely.

export interface BubbleLike {
  role: string;
  error?: boolean;
}

export interface StreamState {
  /** A turn is in flight. */
  streaming: boolean;
  /** The session that turn belongs to. */
  streamingId: string | null;
  /** The session currently on screen. */
  selectedId: string | null;
}

/**
 * Index of the bubble to mark as working, or -1 when nothing is.
 *
 * The last assistant turn that hasn't failed, because:
 *
 *   - a tool UI arrives as its own bubble mid-turn, and the model is not
 *     writing into it — but it *is* an assistant bubble, and it is the
 *     one the reply continues in, so it is the right place for the
 *     spinner once it is the tail;
 *   - an error bubble means the turn ended, so a spinner on it would
 *     claim work is still happening after the failure that stopped it;
 *   - a turn outlives the selection. Switching sessions mid-turn must
 *     not move the spinner to whatever the other conversation happens to
 *     have ended with.
 */
export function liveBubbleIndex(
  messages: readonly BubbleLike[],
  { streaming, streamingId, selectedId }: StreamState,
): number {
  if (!streaming || streamingId !== selectedId) return -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === 'assistant' && !m.error) return i;
  }
  return -1;
}

/** What pressing send does, given where the session currently is. */
export type SendRoute =
  /** Add it to the turn already running. */
  | 'steer'
  /** Hold it; the pod isn't accepting anything yet. */
  | 'queue'
  /** Start a new turn with it. */
  | 'start';

export interface SendState extends StreamState {
  /** Live pod phase for the selected session, null before the first frame. */
  phase: 'provisioning' | 'ready' | 'suspended' | null;
}

/**
 * Route a send. Every state has an answer, because the composer is never
 * disabled — there is no case where the UI can simply decline to have
 * one.
 *
 * `steer` only when the running turn is *this* session's: a turn the user
 * started elsewhere and navigated away from is still streaming, and
 * adding to it would put the message in a conversation they are not
 * looking at.
 *
 * `queue` covers every not-ready phase, `null` (no lifecycle frame yet)
 * included. Guessing 'ready' there would send into a pod that may not
 * exist, and the failure would read to the user as their message
 * vanishing.
 */
export function sendRoute(
  { streaming, streamingId, selectedId, phase }: SendState,
): SendRoute {
  if (streaming && streamingId === selectedId) return 'steer';
  if (phase !== 'ready') return 'queue';
  return 'start';
}

// ── vertical rhythm ─────────────────────────────────────────────────

export interface TurnLike {
  role: string;
  content?: string;
  tool_name?: string;
  tool_ui?: string;
  reasoning?: string;
  tool_error?: boolean;
  tool_running?: boolean;
}

/**
 * Whether a turn is a log line rather than something someone said.
 *
 * A tool call with no prose and no UI of its own renders as one collapsed
 * row. It is evidence, not conversation, and a run of them should read as
 * a list — not as ten separate contributions to the discussion.
 */
export function isCompactTurn(turn: TurnLike): boolean {
  if (turn.role === 'user') return false;
  if ((turn.content ?? '').trim() || (turn.tool_ui ?? '').trim()) return false;
  // A tool call, or a step that only thought: either way the turn draws as
  // one collapsed row ("› bash", "› Thinking").
  return !!turn.tool_name || !!(turn.reasoning ?? '').trim();
}

/**
 * Space above a turn, in rem.
 *
 * A single uniform gap can't work now that every tool call is its own
 * turn. Sized for conversation, a transcript of twenty collapsed tool
 * rows becomes mostly whitespace; sized for those rows, replies collide
 * with each other. So the gap is a function of what sits on both sides of
 * it: log-to-log is tight, conversation-to-conversation gets the full
 * measure, and a boundary between the two gets something in between.
 */
export function turnGapRem(
  turns: readonly TurnLike[],
  index: number,
): number {
  if (index <= 0) return 0;
  const compact = isCompactTurn(turns[index]);
  const prevCompact = isCompactTurn(turns[index - 1]);
  if (compact && prevCompact) return 0.15;
  if (compact || prevCompact) return 0.4;
  // A change of speaker gets a little more than two replies in a row.
  if (turns[index].role === 'user' || turns[index - 1].role === 'user') return 1.25;
  return 1;
}

// ── tool runs ───────────────────────────────────────────────────────

/** One row of the transcript: a turn on its own, or a run of tool calls. */
export type TranscriptSegment =
  | { kind: 'turn'; index: number }
  | { kind: 'run'; indices: number[] };

/** Tool calls a run needs before it is folded into one summary row. */
const MIN_RUN_TOOLS = 2;

/**
 * Fold consecutive collapsed rows into runs. A run is a maximal stretch of
 * compact turns (tool calls, and steps that only thought) holding at least
 * two tool calls; it renders as one "Called 3 tools, read 13 files" row
 * that expands to the rows inside. A stretch with fewer tools stays as is.
 */
export function segmentTranscript(turns: readonly TurnLike[]): TranscriptSegment[] {
  const out: TranscriptSegment[] = [];
  let i = 0;
  while (i < turns.length) {
    if (!isCompactTurn(turns[i])) {
      out.push({ kind: 'turn', index: i });
      i++;
      continue;
    }
    let j = i;
    while (j < turns.length && isCompactTurn(turns[j])) j++;
    const indices = Array.from({ length: j - i }, (_, k) => i + k);
    const tools = indices.filter((k) => !!turns[k].tool_name).length;
    if (tools >= MIN_RUN_TOOLS) out.push({ kind: 'run', indices });
    else for (const k of indices) out.push({ kind: 'turn', index: k });
    i = j;
  }
  return out;
}

/** What a tool does, as the run summary counts it. */
const TOOL_KINDS: Record<string, 'read' | 'write' | 'edit' | 'command'> = {
  read: 'read',
  write: 'write',
  edit: 'edit',
  bash: 'command',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "Called 3 tools, ran 2 commands, wrote 1 file, read 13 files". */
export function summarizeToolRun(turns: readonly TurnLike[]): string {
  const n = { tool: 0, command: 0, edit: 0, write: 0, read: 0, failed: 0 };
  for (const t of turns) {
    if (!t.tool_name) continue;
    n[TOOL_KINDS[t.tool_name] ?? 'tool']++;
    if (t.tool_error) n.failed++;
  }
  const parts = [
    n.tool && `called ${plural(n.tool, 'tool', 'tools')}`,
    n.command && `ran ${plural(n.command, 'command', 'commands')}`,
    n.edit && `edited ${plural(n.edit, 'file', 'files')}`,
    n.write && `wrote ${plural(n.write, 'file', 'files')}`,
    n.read && `read ${plural(n.read, 'file', 'files')}`,
  ].filter(Boolean) as string[];
  let label = parts.join(', ');
  label = label.charAt(0).toUpperCase() + label.slice(1);
  if (n.failed) label += ` (${n.failed} failed)`;
  return label;
}

// ── embedded images ─────────────────────────────────────────────────

/** One run of a message body: verbatim text, or an image to draw. */
export type ContentPart =
  | { kind: 'text'; text: string }
  | { kind: 'image'; src: string; alt: string };

// Pasting a screenshot into the composer lands in ProseMirror as an image
// node, and Milkdown serializes that to a commonmark image whose URL is the
// entire `data:image/…;base64,…` payload. So the message the user sends is
// text with a megabyte of base64 in the middle of it.
//
// The scheme allowlist matches lib/web/Markdown.tsx's urlTransform: raster
// formats only. SVG is excluded because it can carry markup, and every
// other `data:` (data:text/html, and so on) stays inert text.
const EMBEDDED_IMAGE =
  /!\[([^\]]*)\]\(\s*<?(data:image\/(?:png|jpe?g|gif|webp|avif)[;,][^)\s<>"]+)>?(?:\s+"[^"]*")?\s*\)/gi;

/**
 * Split a message body into text runs and the images embedded in it.
 *
 * The user's turn renders verbatim — markdown would swallow the
 * underscores and asterisks in a message that is as often a path or a
 * command as it is prose. But verbatim also meant a pasted screenshot
 * printed as its own base64, which is neither readable nor what the user
 * put in the box. So images come out as parts of their own and everything
 * between them is still shown exactly as written.
 *
 * A body with no image yields a single text part, which is the same thing
 * the old rendering did.
 */
export function splitEmbeddedImages(content: string): ContentPart[] {
  const parts: ContentPart[] = [];
  let cut = 0;
  // The pattern is module-level and /g carries lastIndex between calls.
  EMBEDDED_IMAGE.lastIndex = 0;
  for (let m = EMBEDDED_IMAGE.exec(content); m; m = EMBEDDED_IMAGE.exec(content)) {
    if (m.index > cut) parts.push({ kind: 'text', text: content.slice(cut, m.index) });
    parts.push({ kind: 'image', src: m[2], alt: m[1] });
    cut = m.index + m[0].length;
  }
  if (cut < content.length) parts.push({ kind: 'text', text: content.slice(cut) });
  return parts;
}

// ── sidebar order ───────────────────────────────────────────────────

/** The fields the session list orders and dates each row by. */
export interface RecencyLike {
  last_message_at?: string;
  updated_at?: string;
}

/**
 * The timestamp a session sorts and displays by: when it last said
 * something, or — for one that has never had a turn — when its row was
 * last written.
 *
 * The fallback is what keeps a session the user created five seconds ago
 * from sorting under everything they have ever talked to. Mirrors
 * store.SessionRecency on the server, which is what makes the order the
 * SPA renders the same one the manager already sorted the list into.
 */
export function sessionRecency(session: RecencyLike): number {
  const stamp = session.last_message_at || session.updated_at;
  if (!stamp) return 0;
  const ms = Date.parse(stamp);
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Comparator putting the most recently talked-in conversation first.
 *
 * The list arrives from the manager already in this order, so this is not
 * about the first paint — it is about every one after it. Rows come in
 * over the socket one at a time and are applied in place, and a session
 * that just took a turn would otherwise sit wherever it happened to be
 * when the page loaded until the next full reload.
 */
export function byRecency(a: RecencyLike, b: RecencyLike): number {
  return sessionRecency(b) - sessionRecency(a);
}

const SECOND_MS = 1_000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

// English abbreviations rather than toLocaleDateString: the label sits in
// a badge, and a locale that spells the month long ("sept.", "déc.") or
// reorders the fields would overflow it.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A session's age as the sidebar badge prints it: `30s`, `5m`, `1h`,
 * `3d`, then `25 Aug` within the current year and `Aug 2025` before it.
 *
 * Short enough to lead a one-line pill in a pane the user can drag down
 * to a couple of hundred pixels, so: a unit and a number, never two. The
 * exact time is the tooltip's job — at this size the question is "was
 * this this morning or last month", and "3h" answers it in two
 * characters.
 *
 * Anything a week old or more gets a date instead of a count, because
 * "42d" is a number nobody converts back into a day. The date drops the
 * day for earlier years: the year is the information there, and a full
 * "25 Aug 2025" would not fit the badge. Calendar fields are local time,
 * matching the tooltip's toLocaleString.
 */
export function formatRecency(stamp: number, now: number): string {
  if (!stamp) return '';
  // A clock skewed a little into the future reads as now, not as a
  // negative age. The manager stamps these, so a browser running a
  // minute fast is enough to produce one.
  const elapsed = Math.max(0, now - stamp);
  if (elapsed < MINUTE_MS) return `${Math.floor(elapsed / SECOND_MS)}s`;
  if (elapsed < HOUR_MS) return `${Math.floor(elapsed / MINUTE_MS)}m`;
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h`;
  if (elapsed < WEEK_MS) return `${Math.floor(elapsed / DAY_MS)}d`;
  const d = new Date(stamp);
  const month = MONTHS[d.getMonth()];
  if (d.getFullYear() === new Date(now).getFullYear()) return `${d.getDate()} ${month}`;
  return `${month} ${d.getFullYear()}`;
}

/**
 * Index of the newest message that carries a thinking trace, or -1. Every
 * thinking block above it has been superseded: the model has moved on to
 * newer thinking, so the older block closes unless the user is engaged
 * with it (MessageTurn).
 */
export function latestReasoningIndex(messages: readonly { role: string; reasoning?: string }[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== 'user' && (m.reasoning ?? '').length > 0) return i;
  }
  return -1;
}
